import { Box, Chip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ParagraphDto } from '../../api/queries/books';
import { useReorderParagraphs } from '../../api/queries/paragraphs';
import { useEntityNames } from '../../hooks/useEntityNames';
import { AIContextMenu } from '../ai/AIContextMenu';
import { ParagraphInlineMedia } from './ParagraphInlineMedia';

interface Props {
  paragraphs: ParagraphDto[];
  selectedParagraphId: string | null;
  onSelect: (id: string) => void;
  bookId?: string;
  chapterId?: string;
  onGenerateImage?: () => void;
  onGenerateAudio?: () => void;
}

/**
 * Switches to react-window virtualization at >200 paragraphs. Below that the native
 * list is fine and keeps the right-click context menu predictable. 05 D-012: selected
 * paragraph gets the primary border. Inline images appear below each paragraph.
 *
 * Sprint 4: drag-to-reorder via @dnd-kit/sortable (Read view only).
 */
export function ParagraphList({
  paragraphs,
  selectedParagraphId,
  onSelect,
  bookId,
  chapterId,
  onGenerateImage,
  onGenerateAudio,
}: Props) {
  const virtualize = paragraphs.length > 200;
  const containerRef = useRef<HTMLDivElement>(null);
  const reorder = useReorderParagraphs(chapterId);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const [order, setOrder] = useState(paragraphs);

  useEffect(() => {
    setOrder(paragraphs);
  }, [paragraphs]);

  const handleGenerateImage = onGenerateImage ?? (() => {});
  const handleGenerateAudio = onGenerateAudio ?? (() => {});

  const ids = useMemo(() => order.map((p) => p.id), [order]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);
    reorder.mutate(next.map((p) => p.id));
  };

  if (paragraphs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No paragraphs yet in this chapter.
      </Typography>
    );
  }

  if (virtualize) {
    return (
      <FixedSizeList
        height={1200}
        width="100%"
        itemSize={240}
        itemCount={order.length}
      >
        {({ index, style }) => (
          <div style={style}>
            <ParagraphItem
              paragraph={order[index]!}
              isSelected={order[index]!.id === selectedParagraphId}
              onSelect={onSelect}
              bookId={bookId}
              onGenerateImage={handleGenerateImage}
              onGenerateAudio={handleGenerateAudio}
            />
          </div>
        )}
      </FixedSizeList>
    );
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <Box ref={containerRef}>
          {order.map((p) => (
            <SortableParagraphItem
              key={p.id}
              paragraph={p}
              isSelected={p.id === selectedParagraphId}
              onSelect={onSelect}
              bookId={bookId}
              onGenerateImage={handleGenerateImage}
              onGenerateAudio={handleGenerateAudio}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

interface ParagraphItemProps {
  paragraph: ParagraphDto;
  isSelected: boolean;
  onSelect: (id: string) => void;
  bookId?: string;
  onGenerateImage: () => void;
  onGenerateAudio: () => void;
}

function SortableParagraphItem(props: ParagraphItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.paragraph.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          color: 'text.disabled',
          mt: 2.5,
          mr: 0.5,
          '&:hover': { color: 'text.secondary' },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ParagraphItem {...props} />
      </Box>
    </Box>
  );
}

function ParagraphItem({
  paragraph,
  isSelected,
  onSelect,
  bookId,
  onGenerateImage,
  onGenerateAudio,
}: ParagraphItemProps) {
  const speakerIds = paragraph.speakingEntityIds ?? [];
  const entityNames = useEntityNames(speakerIds, bookId);

  return (
    <AIContextMenu
      contextParams={{
        entity_type: 'paragraph',
        entity_id: paragraph.id,
        book_id: bookId,
        scope: bookId ? `book:${bookId}` : undefined,
      }}
    >
      <Box
        data-paragraph-id={paragraph.id}
        sx={{
          p: 2,
          border: (theme) =>
            isSelected
              ? `2px solid ${theme.palette.primary.main}`
              : '1px solid transparent',
          borderRadius: 1,
          cursor: 'pointer',
        }}
        onClick={() => onSelect(paragraph.id)}
      >
        {speakerIds.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            {speakerIds.map((id) => (
              <Chip
                key={id}
                size="small"
                icon={<RecordVoiceOverIcon sx={{ fontSize: 14 }} />}
                label={entityNames.get(id) ?? 'Unknown'}
                variant="outlined"
                sx={{ mr: 0.5, fontSize: 11 }}
              />
            ))}
          </Box>
        )}
        <Box dangerouslySetInnerHTML={{ __html: paragraph.contentHtml }} />
        <ParagraphInlineMedia
          paragraphId={paragraph.id}
          onGenerateImage={onGenerateImage}
          onGenerateAudio={onGenerateAudio}
        />
      </Box>
    </AIContextMenu>
  );
}
